import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CollectionActivityWithUser,
  CollectionActivityType,
  Buyer,
  PromiseToPayCreate,
  PROMISE_TO_PAY_TYPE_LABELS,
  PROMISE_TO_PAY_REASON_LABELS,
} from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import {
  getBuyerCollectionActivities,
  updateCollectionActivity,
  deleteCollectionActivity,
} from '../../../../redux/services/collectionActivityService';
import { createPromiseToPay } from '../../../../redux/services/promiseToPayService';
import { getGroupedSuppliersInvoices } from '../../../../redux/services/invoicesService';
import {
  Phone,
  ChatCircleText,
  ChatCircleDots,
  Check,
  CheckCircle,
  Play,
  Star,
  X,
  FileText,
  Bell,
  Warning,
  CurrencyDollar,
  ArrowUUpLeft,
  DotsThree,
  PencilSimple,
  Envelope,
  EnvelopeSimple,
  ArrowSquareOut,
  Calendar,
  Buildings,
  AddressBook,
  Plus,
  UserCircle,
  Handshake,
} from 'phosphor-react';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import LexicalViewer from '../../../general/LexicalEditor/LexicalViewer';
import AddNoteModal from './modals/AddNoteModal';
import AddCallModal from './modals/AddCallModal';
import AddTaskModal from './modals/AddTaskModal';
import AddTextMessageModal from './modals/AddTextMessageModal';
import AddEmailModal from './modals/AddEmailModal';
import EditNoteModal from './modals/EditNoteModal';
import EditCallModal from './modals/EditCallModal';
import EditTaskModal from './modals/EditTaskModal';
import EditTextMessageModal from './modals/EditTextMessageModal';
import { PromiseToPayFormModal } from '../../customers/CustomerDetails/SupplierCustomerPromiseToPays/PromiseToPayFormModal';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { APP_ENVIRONMENT } from '../../../../utils/constants';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillCheckbox, DillDropdown } from '../../../general/Dill';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface CollectionActivityTimelineProps {
  buyerId: string;
  buyer?: Buyer | null;
  filterType?: CollectionActivityType | 'ALL' | 'EMAILS';
  filterMode?: 'ALL' | 'MANUAL_ONLY';
  selectedJobIds?: string[];
  onViewPaymentDetails?: (transactionId: string) => void;
  onViewPaymentRequest?: (paymentRequestId: string) => void;
  onViewDisputeDetails?: (disputeId: string) => void;
  onViewPromiseToPay?: (promiseToPayId: string) => void;
  hideActions?: boolean;
}

const MANUAL_ACTIVITY_TYPES: CollectionActivityType[] = [
  'PHONE_CALL',
  'TEXT_MESSAGE',
  'NOTE',
  'TASK',
];

const EMAIL_ACTIVITY_TYPES: CollectionActivityType[] = [
  'PAYMENT_REQUEST',
  'PAYMENT_REMINDER',
  'STATEMENT',
];

const CALL_OUTCOME_LABELS: Record<string, string> = {
  REACHED: 'Reached',
  VOICEMAIL: 'Voicemail',
  NO_ANSWER: 'No Answer',
  BUSY: 'Busy',
  FAILED: 'Failed',
};

const TEXT_MESSAGE_OUTCOME_LABELS: Record<string, string> = {
  DELIVERED: 'Delivered',
  READ: 'Read',
  NO_RESPONSE: 'No Response',
  FAILED: 'Failed',
};

/** Format duration in seconds to a readable string */
const formatDuration = (seconds: number | undefined): string => {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/** Format date relative to now for recent activities, full date for older */
const formatRelativeDate = (date: Date | string | undefined): string => {
  if (!date) return '';
  const m = moment(new Date(date));
  const now = moment();
  const diffHours = now.diff(m, 'hours');

  if (diffHours < 1) {
    const diffMins = now.diff(m, 'minutes');
    if (diffMins < 1) return 'Just now';
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return `Yesterday at ${m.format('h:mm A')}`;
  if (diffHours < 168) return m.format('ddd [at] h:mm A'); // Within a week
  return m.format('MMM D, YYYY [at] h:mm A');
};

const activityTypeConfig: Record<
  CollectionActivityType,
  { icon: React.ReactNode; color: string; ringColor: string; label: string }
> = {
  PAYMENT_REQUEST: {
    icon: <Bell size={16} weight="fill" />,
    color: 'bg-blue-600 text-white',
    ringColor: 'ring-blue-200',
    label: 'Payment Request',
  },
  PAYMENT_REMINDER: {
    icon: <Bell size={16} weight="fill" />,
    color: 'bg-blue-600 text-white',
    ringColor: 'ring-blue-200',
    label: 'Payment Reminder',
  },
  PAYMENT: {
    icon: <CurrencyDollar size={16} weight="fill" />,
    color: 'bg-emerald-600 text-white',
    ringColor: 'ring-emerald-200',
    label: 'Payment',
  },
  REFUND: {
    icon: <ArrowUUpLeft size={16} weight="fill" />,
    color: 'bg-orange-600 text-white',
    ringColor: 'ring-orange-200',
    label: 'Refund',
  },
  STATEMENT: {
    icon: <FileText size={16} weight="fill" />,
    color: 'bg-violet-600 text-white',
    ringColor: 'ring-violet-200',
    label: 'Statement',
  },
  DISPUTE: {
    icon: <Warning size={16} weight="fill" />,
    color: 'bg-rose-600 text-white',
    ringColor: 'ring-rose-200',
    label: 'Dispute',
  },
  PHONE_CALL: {
    icon: <Phone size={16} weight="fill" />,
    color: 'bg-amber-600 text-white',
    ringColor: 'ring-amber-200',
    label: 'Phone Call',
  },
  TEXT_MESSAGE: {
    icon: <ChatCircleDots size={16} weight="fill" />,
    color: 'bg-teal-600 text-white',
    ringColor: 'ring-teal-200',
    label: 'Text Message',
  },
  NOTE: {
    icon: <ChatCircleText size={16} weight="fill" />,
    color: 'bg-slate-500 text-white',
    ringColor: 'ring-slate-200',
    label: 'Note',
  },
  TASK: {
    icon: <CheckCircle size={16} weight="fill" />,
    color: 'bg-indigo-600 text-white',
    ringColor: 'ring-indigo-200',
    label: 'Task',
  },
  PROMISE_TO_PAY: {
    icon: <Handshake size={16} weight="fill" />,
    color: 'bg-cyan-600 text-white',
    ringColor: 'ring-cyan-200',
    label: 'Promise to Pay',
  },
};

function CollectionActivityTimeline({
  buyerId,
  buyer,
  filterMode = 'ALL',
  selectedJobIds,
  onViewPaymentDetails,
  onViewPaymentRequest,
  onViewDisputeDetails,
  onViewPromiseToPay,
  hideActions = false,
}: CollectionActivityTimelineProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { buyerActivities, loadingList } = useAppSelector((state) => state.collectionActivities);
  const { loadingList: promiseToPayLoadingList } = useAppSelector((state) => state.promiseToPays);
  const { groupedSuppliersInvoices } = useAppSelector((state) => state.invoice);
  const [filterType, setFilterType] = useState<CollectionActivityType | 'ALL' | 'EMAILS'>('ALL');

  // Reference filter state
  const [selectedInvoiceRefs, setSelectedInvoiceRefs] = useState<string[] | null>(null);
  const [selectedJobRefs, setSelectedJobRefs] = useState<string[] | null>(null);

  // Modal states
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isAddCallModalOpen, setIsAddCallModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddTextMessageModalOpen, setIsAddTextMessageModalOpen] = useState(false);
  const [isAddPromiseModalOpen, setIsAddPromiseModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CollectionActivityWithUser | null>(null);
  const [editingCall, setEditingCall] = useState<CollectionActivityWithUser | null>(null);
  const [editingTask, setEditingTask] = useState<CollectionActivityWithUser | null>(null);
  const [editingTextMessage, setEditingTextMessage] = useState<CollectionActivityWithUser | null>(
    null
  );
  const [addTypeMenuOpen, setAddTypeMenuOpen] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const [updatingAction, setUpdatingAction] = useState<
    'start' | 'complete' | 'accept' | 'decline' | null
  >(null);
  const [expandedEmailIds, setExpandedEmailIds] = useState<Set<string>>(new Set());

  const toggleEmailPreview = (activityId: string) => {
    setExpandedEmailIds((prev) => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  };

  // Phone picker popover state
  const [isPhonePickerOpen, setIsPhonePickerOpen] = useState(false);
  const phonePickerAnchorRef = useRef<HTMLDivElement>(null);
  const phonePickerRef = useRef<HTMLDivElement>(null);
  const [manualPhoneInput, setManualPhoneInput] = useState('');
  const [makeCallPhoneNumber, setMakeCallPhoneNumber] = useState<string | null>(null);

  // Text picker popover state
  const [isTextPickerOpen, setIsTextPickerOpen] = useState(false);
  const textPickerAnchorRef = useRef<HTMLDivElement>(null);
  const textPickerRef = useRef<HTMLDivElement>(null);
  const [manualTextPhoneInput, setManualTextPhoneInput] = useState('');
  const [sendTextPhoneNumber, setSendTextPhoneNumber] = useState<string | null>(null);

  // Email picker popover state
  const [isEmailPickerOpen, setIsEmailPickerOpen] = useState(false);
  const emailPickerAnchorRef = useRef<HTMLDivElement>(null);
  const emailPickerRef = useRef<HTMLDivElement>(null);
  const [manualEmailInput, setManualEmailInput] = useState('');
  const [sendEmailAddress, setSendEmailAddress] = useState<string | null>(null);

  // Get buyer's phone numbers / emails from prop
  const buyerPhoneNumbers = buyer?.phoneNumbers ?? [];
  const buyerEmailAddresses = buyer?.quickbooksCustomerEmails ?? [];
  const buyerContacts = buyer?.buyerContacts ?? [];
  const contactsWithPhone = buyerContacts.filter((c) => c.phoneNumber);
  const contactsWithEmail = buyerContacts.filter((c) => c.email);

  // Close phone picker on outside click
  useEffect(() => {
    if (!isPhonePickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        phonePickerRef.current &&
        !phonePickerRef.current.contains(e.target as Node) &&
        phonePickerAnchorRef.current &&
        !phonePickerAnchorRef.current.contains(e.target as Node)
      ) {
        setIsPhonePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPhonePickerOpen]);

  const handleSelectPhoneNumber = (phoneNumber: string) => {
    setMakeCallPhoneNumber(phoneNumber);
    setIsPhonePickerOpen(false);
    setManualPhoneInput('');
  };

  // Close text picker on outside click
  useEffect(() => {
    if (!isTextPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        textPickerRef.current &&
        !textPickerRef.current.contains(e.target as Node) &&
        textPickerAnchorRef.current &&
        !textPickerAnchorRef.current.contains(e.target as Node)
      ) {
        setIsTextPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTextPickerOpen]);

  const handleSelectTextPhoneNumber = (phoneNumber: string) => {
    setSendTextPhoneNumber(phoneNumber);
    setIsTextPickerOpen(false);
    setManualTextPhoneInput('');
  };

  // Close email picker on outside click
  useEffect(() => {
    if (!isEmailPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        emailPickerRef.current &&
        !emailPickerRef.current.contains(e.target as Node) &&
        emailPickerAnchorRef.current &&
        !emailPickerAnchorRef.current.contains(e.target as Node)
      ) {
        setIsEmailPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEmailPickerOpen]);

  const handleSelectEmailAddress = (emailAddress: string) => {
    setSendEmailAddress(emailAddress);
    setIsEmailPickerOpen(false);
    setManualEmailInput('');
  };

  // Date range state - default to last 3 months
  const [dateRange, setDateRange] = useState<{ startDate?: Date; endDate?: Date }>(() => ({
    startDate: moment().subtract(3, 'months').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  }));
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

  const handleCreatePromise = async (data: PromiseToPayCreate) => {
    if (!currentSupplier?.id) return;
    await dispatch(createPromiseToPay({ supplierId: currentSupplier.id, data }));
    setIsAddPromiseModalOpen(false);
    // The promise is also logged as a PROMISE_TO_PAY collection activity on the backend -
    // refetch so it shows up in this timeline right away.
    dispatch(
      getBuyerCollectionActivities({
        supplierId: currentSupplier.id,
        buyerId,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      })
    );
  };

  useEffect(() => {
    if (currentSupplier?.id && buyerId) {
      dispatch(
        getBuyerCollectionActivities({
          supplierId: currentSupplier.id,
          buyerId,
          startDate: dateRange?.startDate,
          endDate: dateRange?.endDate,
        })
      );
    }
  }, [currentSupplier?.id, buyerId, dispatch, dateRange]);

  // Fetch invoice/job data for reference filters
  useEffect(() => {
    if (currentSupplier?.id && buyerId) {
      dispatch(
        getGroupedSuppliersInvoices({
          supplierId: currentSupplier.id,
          buyerId,
          weeksLimit: 52,
        })
      );
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  // Build filter options from grouped invoices
  const { invoiceFilterOptions, jobFilterOptions } = useMemo(() => {
    const invoiceMap = new Map<string, { docNumber: string; balance?: number }>();
    const jobMap = new Map<
      string,
      {
        id: string;
        jobName: string;
        jobNumber?: string;
        address?: string;
        city?: string;
        state?: string;
        zipCode?: string;
      }
    >();

    for (const supplier of groupedSuppliersInvoices) {
      const invoices = supplier.invoices;
      if (!invoices) continue;

      const groups = [
        invoices.pastDueInvoices?.invoices,
        invoices.currentInvoices?.invoices,
        invoices.paidInvoices?.invoices,
        invoices.todayInvoices?.invoices,
        invoices.pastSignedInvoices?.invoices,
        invoices.pendingPayInvoices?.invoices,
      ];

      for (const group of groups) {
        if (!group) continue;
        for (const inv of group) {
          if (!invoiceMap.has(inv.id)) {
            invoiceMap.set(inv.id, {
              docNumber: inv.docNumber,
              balance: inv.balance,
            });
          }
          if (inv.quickBooksJob && inv.quickBooksJob.id && !jobMap.has(inv.quickBooksJob.id)) {
            jobMap.set(inv.quickBooksJob.id, {
              id: inv.quickBooksJob.id,
              jobName: inv.quickBooksJob.jobName,
              jobNumber: inv.quickBooksJob.jobNumber || undefined,
              address: inv.quickBooksJob.address || undefined,
              city: inv.quickBooksJob.city || undefined,
              state: inv.quickBooksJob.state || undefined,
              zipCode: inv.quickBooksJob.zipCode || undefined,
            });
          }
        }
      }
    }

    const invoiceOpts = Array.from(invoiceMap.entries()).map(([id, data]) => ({
      value: id,
      label: `INV-${data.docNumber}${
        data.balance !== undefined && data.balance !== null ? ` ($${data.balance.toFixed(2)})` : ''
      }`,
    }));

    const jobOpts = Array.from(jobMap.values()).map((job) => {
      const jobAddress = `${job.address ?? ''} ${job.city ?? ''} ${
        job.state ? `${job.state},` : ''
      } ${job.zipCode ?? ''}`.trim();
      return {
        value: job.id,
        label: `${job.jobName}${job.jobNumber ? ` (${job.jobNumber})` : ''}`,
        address: jobAddress || undefined,
      };
    });

    return { invoiceFilterOptions: invoiceOpts, jobFilterOptions: jobOpts };
  }, [groupedSuppliersInvoices]);

  const isLoading = loadingList.includes('getBuyerCollectionActivities');

  // Compute filter counts for badge display
  const filterCounts = useMemo(() => {
    const modeFiltered =
      filterMode === 'MANUAL_ONLY'
        ? buyerActivities.filter((a) => MANUAL_ACTIVITY_TYPES.includes(a.activityType))
        : buyerActivities;
    return {
      ALL: modeFiltered.length,
      NOTE: modeFiltered.filter((a) => a.activityType === 'NOTE').length,
      PHONE_CALL: modeFiltered.filter((a) => a.activityType === 'PHONE_CALL').length,
      TEXT_MESSAGE: modeFiltered.filter((a) => a.activityType === 'TEXT_MESSAGE').length,
      TASK: modeFiltered.filter((a) => a.activityType === 'TASK').length,
      EMAILS: modeFiltered.filter((a) => EMAIL_ACTIVITY_TYPES.includes(a.activityType)).length,
    };
  }, [buyerActivities, filterMode]);

  const filteredActivities = buyerActivities.filter((activity) => {
    // First apply mode filter
    if (filterMode === 'MANUAL_ONLY' && !MANUAL_ACTIVITY_TYPES.includes(activity.activityType)) {
      return false;
    }
    // Then apply type filter
    if (filterType === 'EMAILS') {
      return EMAIL_ACTIVITY_TYPES.includes(activity.activityType);
    }
    if (filterType !== 'ALL' && activity.activityType !== filterType) {
      return false;
    }
    // Reference filters
    if (selectedInvoiceRefs && selectedInvoiceRefs.length > 0) {
      const hasInvoice = activity.references?.some(
        (r) => r.type === 'INVOICE' && selectedInvoiceRefs.includes(r.id)
      );
      if (!hasInvoice) return false;
    }
    if (selectedJobRefs && selectedJobRefs.length > 0) {
      const hasJob = activity.references?.some(
        (r) => r.type === 'JOB' && selectedJobRefs.includes(r.id)
      );
      if (!hasJob) return false;
    }
    const activeExternalJobIds = (selectedJobIds ?? []).filter((id) => id !== 'ALL');
    if (activeExternalJobIds.length > 0) {
      const hasJob = activity.references?.some(
        (r) => r.type === 'JOB' && activeExternalJobIds.includes(r.id)
      );
      if (!hasJob) return false;
    }
    return true;
  });

  const sortedActivities = [...filteredActivities].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA; // Most recent first
  });

  const formatActivityDate = formatRelativeDate;

  // Helper to get content string from Lexical content (object or string)
  const getContentString = (content: unknown): string | null => {
    if (!content) return null;
    if (typeof content === 'string') return content;
    if (typeof content === 'object') return JSON.stringify(content);
    return null;
  };

  const refreshActivities = () => {
    if (currentSupplier?.id && buyerId) {
      dispatch(
        getBuyerCollectionActivities({
          supplierId: currentSupplier.id,
          buyerId,
          startDate: dateRange?.startDate,
          endDate: dateRange?.endDate,
        })
      );
    }
  };

  const handleStartTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('start');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            status: 'IN_PROGRESS',
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
    refreshActivities();
  };

  const handleCompleteTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    if (!user?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('complete');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            status: 'COMPLETED',
            completedAt: new Date(),
            completedBy: user.id,
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
    refreshActivities();
  };

  const handleAcceptTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('accept');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            isDillRecommended: false,
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
    refreshActivities();
  };

  const handleDeclineTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('decline');
    await dispatch(
      deleteCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
    refreshActivities();
  };

  const getActivityDescription = (activity: CollectionActivityWithUser): string => {
    switch (activity.activityType) {
      case 'PAYMENT':
        if (activity.paymentDetails) {
          const pd = activity.paymentDetails;
          // Bank payment-request payments settle asynchronously, so they are logged at initiation.
          const isBankInitiated = pd.paymentMethod === 'BANK' && !!pd.paymentRequestId;
          return `Payment of $${pd.amount.toFixed(2)} ${
            isBankInitiated ? 'initiated' : 'received'
          } via ${pd.paymentMethod}`;
        }
        return 'Payment received';
      case 'REFUND':
        return activity.refundDetails
          ? `${
              activity.refundDetails.isPartialRefund ? 'Partial refund' : 'Refund'
            } of $${activity.refundDetails.amount.toFixed(2)} issued via ${
              activity.refundDetails.paymentMethod
            }`
          : 'Refund issued';
      case 'PHONE_CALL':
        if (activity.callDetails) {
          const outcome =
            CALL_OUTCOME_LABELS[activity.callDetails.outcome] || activity.callDetails.outcome;
          const duration = formatDuration(activity.callDetails.duration ?? undefined);
          const parts = [`Call to ${activity.callDetails.phoneNumber}`, outcome];
          if (duration) parts.push(duration);
          return parts.join(' \u00B7 ');
        }
        return 'Phone call logged';
      case 'TEXT_MESSAGE':
        if (activity.textMessageDetails) {
          const outcome =
            TEXT_MESSAGE_OUTCOME_LABELS[activity.textMessageDetails.outcome] ||
            activity.textMessageDetails.outcome;
          return `Text to ${activity.textMessageDetails.phoneNumber} \u00B7 ${outcome}`;
        }
        return 'Text message logged';
      case 'NOTE':
        return '';
      case 'TASK':
        return activity.taskDetails?.title || 'Task created';
      case 'DISPUTE':
        return activity.disputeDetails
          ? `Dispute ${activity.disputeDetails.status.toLowerCase()}`
          : 'Dispute updated';
      case 'PAYMENT_REQUEST':
      case 'PAYMENT_REMINDER':
        if (activity.reminderDetails) {
          const invoiceCount = activity.reminderDetails.invoiceIds?.length || 0;
          const invoiceText = invoiceCount === 1 ? '1 invoice' : `${invoiceCount} invoices`;
          const recipientText = activity.reminderDetails.recipientEmail
            ? ` to ${activity.reminderDetails.recipientEmail}`
            : '';
          const invoicePart = invoiceCount > 0 ? ` (${invoiceText})` : '';
          // Payment request lifecycle events use the `action` field.
          const action = activity.reminderDetails.action;
          if (action) {
            const channelText = ` via ${activity.reminderDetails.channel}`;
            switch (action) {
              case 'CREATED':
                return `Payment request created${channelText}${recipientText}`;
              case 'UPDATED':
                return `Payment request edited${channelText}${recipientText}`;
              case 'RESENT':
                return `Payment request resent${channelText}${recipientText}`;
              case 'DELETED':
                return 'Payment request deleted';
              default:
                break;
            }
          }
          return `${activity.reminderDetails.reminderType} sent via ${activity.reminderDetails.channel}${recipientText}${invoicePart}`;
        }
        return 'Reminder sent';
      case 'STATEMENT':
        if (activity.reminderDetails) {
          const recipientText = activity.reminderDetails.recipientEmail
            ? ` to ${activity.reminderDetails.recipientEmail}`
            : '';
          return `${activity.reminderDetails.reminderType} sent via ${activity.reminderDetails.channel}${recipientText}`;
        }
        return 'Statement sent';
      case 'PROMISE_TO_PAY': {
        if (!activity.promiseToPayDetails) return 'Promise to pay logged';
        const { type, amount, promisedPaymentDate, reason, reasonOther } =
          activity.promiseToPayDetails;
        const typeText = type ? PROMISE_TO_PAY_TYPE_LABELS[type] ?? type : 'Payment';
        const amountText = amount != null ? `$${amount.toFixed(2)}` : '';
        const dateText = promisedPaymentDate
          ? new Date(promisedPaymentDate).toLocaleDateString()
          : null;
        const reasonText =
          reason === 'OTHER' && reasonOther
            ? reasonOther
            : reason
            ? PROMISE_TO_PAY_REASON_LABELS[reason] ?? reason
            : null;
        return [
          `${typeText}${amountText ? ` of ${amountText}` : ''}`,
          dateText ? `by ${dateText}` : null,
          reasonText ? `· ${reasonText}` : null,
        ]
          .filter(Boolean)
          .join(' ');
      }
      default:
        return 'Activity logged';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with action buttons */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <DillButton
            label={
              dateRange?.startDate && dateRange?.endDate
                ? `${moment(dateRange.startDate).format('MMM D, YYYY')} – ${moment(
                    dateRange.endDate
                  ).format('MMM D, YYYY')}`
                : 'Select Date Range'
            }
            icon={<Calendar size={14} />}
            onClick={() => setIsDateRangeModalOpen(true)}
            variant="secondary"
            height={36}
          />

          {/* Invoice reference filter */}
          <div className="relative ">
            <DillDropdown
              inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
              placeholder="Invoices"
              isSearchable={false}
              isCreatable={false}
              closeMenu={false}
              customViewElement={
                <div className="flex my-1 px-2 items-center">
                  <FileText size={16} className="text-purple-500" />
                  <p className="text-sm ml-2 text-gray-600">Invoices Filter</p>
                </div>
              }
              itemWidth={280}
              items={invoiceFilterOptions.map((option) => ({
                label: option.label,
                value: option.value,
                labelInElement: (
                  <div className="flex m-1 items-center gap-1">
                    <DillCheckbox
                      isChecked={selectedInvoiceRefs?.includes(option.value) ?? false}
                    />
                    <FileText size={16} className="text-purple-500 flex-shrink-0" />
                    <div className="text-black text-xs flex-1 truncate">{option.label}</div>
                  </div>
                ),
              }))}
              isMulti
              onMultiSelectChange={(data, oldMultiValue) => {
                if (data) {
                  const valueList = data.map((item) => item.value);
                  const valueListWithoutAll = valueList.filter((item) => item !== 'ALL');
                  if (
                    !valueList.includes('ALL') &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL') &&
                    valueListWithoutAll.length === invoiceFilterOptions.length
                  ) {
                    setSelectedInvoiceRefs([]);
                  } else if (
                    valueList.includes('ALL') &&
                    valueListWithoutAll.length < invoiceFilterOptions.length &&
                    oldMultiValue &&
                    oldMultiValue.includes('ALL')
                  ) {
                    setSelectedInvoiceRefs(valueListWithoutAll);
                  } else if (
                    (valueList.includes('ALL') &&
                      valueListWithoutAll.length === 0 &&
                      oldMultiValue &&
                      !oldMultiValue.includes('ALL')) ||
                    valueList.includes('ALL') ||
                    (valueListWithoutAll.length === invoiceFilterOptions.length &&
                      !valueList.includes('ALL'))
                  ) {
                    setSelectedInvoiceRefs(invoiceFilterOptions.map((item) => item.value));
                  } else if (valueList.length > 0) {
                    if (
                      invoiceFilterOptions.length - 1 === valueList.length &&
                      oldMultiValue &&
                      !oldMultiValue.includes('ALL')
                    ) {
                      setSelectedInvoiceRefs(invoiceFilterOptions.map((item) => item.value));
                    } else if (
                      invoiceFilterOptions.length - 1 === valueList.length &&
                      oldMultiValue &&
                      oldMultiValue.includes('ALL')
                    ) {
                      setSelectedInvoiceRefs(null);
                    } else {
                      setSelectedInvoiceRefs(valueList);
                    }
                  } else {
                    setSelectedInvoiceRefs(null);
                  }
                }
              }}
              value={null}
              multiValue={selectedInvoiceRefs}
            />
            {selectedInvoiceRefs && selectedInvoiceRefs.length > 0 && (
              <div className="flex absolute bottom-6 right-[-12px] bg-purple-500 text-white rounded-full text-xs font-bold h-5 w-5 items-center justify-center">
                {selectedInvoiceRefs.length === invoiceFilterOptions.length
                  ? selectedInvoiceRefs.length - 1
                  : selectedInvoiceRefs.length}
              </div>
            )}
          </div>

          {/* Job reference filter */}
          {jobFilterOptions.length > 0 && (
            <div className="relative ">
              <DillDropdown
                inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
                placeholder="Jobs"
                isSearchable={false}
                isCreatable={false}
                closeMenu={false}
                customViewElement={
                  <div className="flex my-1 px-2 items-center">
                    <Buildings size={16} className="text-blue-500" />
                    <p className="text-sm ml-2 text-gray-600">Jobs Filter</p>
                  </div>
                }
                itemWidth={300}
                items={jobFilterOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                  labelInElement: (
                    <div className="flex mb-1 items-center gap-1">
                      <DillCheckbox isChecked={selectedJobRefs?.includes(option.value) ?? false} />
                      <Buildings size={16} className="text-blue-400 flex-shrink-0" />
                      <div className="flex-1 truncate">
                        <div className="text-black text-xs truncate">{option.label}</div>
                        {option.address && (
                          <div className="text-gray-500 text-2xs truncate">{option.address}</div>
                        )}
                      </div>
                    </div>
                  ),
                }))}
                isMulti
                onMultiSelectChange={(data, oldMultiValue) => {
                  if (data) {
                    const valueList = data.map((item) => item.value);
                    const valueListWithoutAll = valueList.filter((item) => item !== 'ALL');
                    if (
                      !valueList.includes('ALL') &&
                      oldMultiValue &&
                      oldMultiValue.includes('ALL') &&
                      valueListWithoutAll.length === jobFilterOptions.length
                    ) {
                      setSelectedJobRefs([]);
                    } else if (
                      valueList.includes('ALL') &&
                      valueListWithoutAll.length < jobFilterOptions.length &&
                      oldMultiValue &&
                      oldMultiValue.includes('ALL')
                    ) {
                      setSelectedJobRefs(valueListWithoutAll);
                    } else if (
                      (valueList.includes('ALL') &&
                        valueListWithoutAll.length === 0 &&
                        oldMultiValue &&
                        !oldMultiValue.includes('ALL')) ||
                      valueList.includes('ALL') ||
                      (valueListWithoutAll.length === jobFilterOptions.length &&
                        !valueList.includes('ALL'))
                    ) {
                      setSelectedJobRefs(jobFilterOptions.map((item) => item.value));
                    } else if (valueList.length > 0) {
                      if (
                        jobFilterOptions.length - 1 === valueList.length &&
                        oldMultiValue &&
                        !oldMultiValue.includes('ALL')
                      ) {
                        setSelectedJobRefs(jobFilterOptions.map((item) => item.value));
                      } else if (
                        jobFilterOptions.length - 1 === valueList.length &&
                        oldMultiValue &&
                        oldMultiValue.includes('ALL')
                      ) {
                        setSelectedJobRefs(null);
                      } else {
                        setSelectedJobRefs(valueList);
                      }
                    } else {
                      setSelectedJobRefs(null);
                    }
                  }
                }}
                value={null}
                multiValue={selectedJobRefs}
              />
              {selectedJobRefs && selectedJobRefs.length > 0 && (
                <div className="flex absolute bottom-6 right-[-12px] bg-blue-500 text-white rounded-full text-xs font-bold h-5 w-5 items-center justify-center">
                  {selectedJobRefs.length === jobFilterOptions.length
                    ? selectedJobRefs.length - 1
                    : selectedJobRefs.length}
                </div>
              )}
            </div>
          )}
        </div>
        {!hideActions && (
          <div className="flex items-center gap-1.5">
            <div className="relative">
              {APP_ENVIRONMENT !== 'production' && (
                <div ref={phonePickerAnchorRef}>
                  <DillButton
                    label="Make Call"
                    icon={<Phone size={14} />}
                    onClick={() => setIsPhonePickerOpen(!isPhonePickerOpen)}
                    variant="primary"
                  />
                </div>
              )}
              {isPhonePickerOpen && (
                <div
                  ref={phonePickerRef}
                  className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-xl border border-gray-200/90 shadow-lg shadow-gray-200/40 z-50 overflow-hidden">
                  {contactsWithPhone.length > 0 && (
                    <div className="py-1.5">
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Contacts
                      </p>
                      {contactsWithPhone.map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <UserCircle size={14} className="text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm text-gray-700 truncate block">
                                {[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                                  'Unknown'}
                              </span>
                              <span className="text-xs text-gray-400 truncate block">
                                {contact.phoneNumber}
                              </span>
                            </div>
                          </div>
                          <DillButton
                            label="Call"
                            onClick={() => handleSelectPhoneNumber(contact.phoneNumber!)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {buyerPhoneNumbers.length > 0 && (
                    <div
                      className={
                        contactsWithPhone.length > 0 ? 'border-t border-gray-100 py-1.5' : 'py-1.5'
                      }>
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Saved Numbers
                      </p>
                      {buyerPhoneNumbers.map((num) => (
                        <div
                          key={num}
                          className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Phone size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{num}</span>
                          </div>
                          <DillButton
                            label="Call"
                            onClick={() => handleSelectPhoneNumber(num)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    className={
                      buyerPhoneNumbers.length > 0 || contactsWithPhone.length > 0
                        ? 'border-t border-gray-100'
                        : ''
                    }>
                    <div className="p-3">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Enter Number
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={manualPhoneInput}
                          onChange={(e) => setManualPhoneInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualPhoneInput.trim()) {
                              handleSelectPhoneNumber(manualPhoneInput.trim());
                            }
                          }}
                          placeholder="+1 (555) 123-4567"
                          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all"
                          autoFocus
                        />
                        <DillButton
                          label="Call"
                          onClick={() => {
                            if (manualPhoneInput.trim()) {
                              handleSelectPhoneNumber(manualPhoneInput.trim());
                            }
                          }}
                          isDisabled={!manualPhoneInput.trim()}
                          variant="primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              {APP_ENVIRONMENT !== 'production' && (
                <div ref={textPickerAnchorRef}>
                  <DillButton
                    label="Send Text"
                    icon={<ChatCircleDots size={14} />}
                    onClick={() => setIsTextPickerOpen(!isTextPickerOpen)}
                    variant="primary"
                  />
                </div>
              )}
              {isTextPickerOpen && (
                <div
                  ref={textPickerRef}
                  className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-xl border border-gray-200/90 shadow-lg shadow-gray-200/40 z-50 overflow-hidden">
                  {contactsWithPhone.length > 0 && (
                    <div className="py-1.5">
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Contacts
                      </p>
                      {contactsWithPhone.map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between px-3 py-2 hover:bg-teal-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <UserCircle size={14} className="text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm text-gray-700 truncate block">
                                {[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                                  'Unknown'}
                              </span>
                              <span className="text-xs text-gray-400 truncate block">
                                {contact.phoneNumber}
                              </span>
                            </div>
                          </div>
                          <DillButton
                            label="Text"
                            onClick={() => handleSelectTextPhoneNumber(contact.phoneNumber!)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {buyerPhoneNumbers.length > 0 && (
                    <div
                      className={
                        contactsWithPhone.length > 0 ? 'border-t border-gray-100 py-1.5' : 'py-1.5'
                      }>
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Saved Numbers
                      </p>
                      {buyerPhoneNumbers.map((num) => (
                        <div
                          key={num}
                          className="flex items-center justify-between px-3 py-2 hover:bg-teal-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <ChatCircleDots size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{num}</span>
                          </div>
                          <DillButton
                            label="Text"
                            onClick={() => handleSelectTextPhoneNumber(num)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    className={
                      buyerPhoneNumbers.length > 0 || contactsWithPhone.length > 0
                        ? 'border-t border-gray-100'
                        : ''
                    }>
                    <div className="p-3">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Enter Number
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={manualTextPhoneInput}
                          onChange={(e) => setManualTextPhoneInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualTextPhoneInput.trim()) {
                              handleSelectTextPhoneNumber(manualTextPhoneInput.trim());
                            }
                          }}
                          placeholder="+1 (555) 123-4567"
                          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all"
                          autoFocus
                        />
                        <DillButton
                          label="Text"
                          onClick={() => {
                            if (manualTextPhoneInput.trim()) {
                              handleSelectTextPhoneNumber(manualTextPhoneInput.trim());
                            }
                          }}
                          isDisabled={!manualTextPhoneInput.trim()}
                          variant="primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              {APP_ENVIRONMENT !== 'production' && (
                <div ref={emailPickerAnchorRef}>
                  <DillButton
                    label="Send Email"
                    icon={<EnvelopeSimple size={14} />}
                    onClick={() => setIsEmailPickerOpen(!isEmailPickerOpen)}
                    variant="primary"
                  />
                </div>
              )}
              {isEmailPickerOpen && (
                <div
                  ref={emailPickerRef}
                  className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-xl border border-gray-200/90 shadow-lg shadow-gray-200/40 z-50 overflow-hidden">
                  {contactsWithEmail.length > 0 && (
                    <div className="py-1.5">
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Contacts
                      </p>
                      {contactsWithEmail.map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <UserCircle size={14} className="text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm text-gray-700 truncate block">
                                {[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                                  'Unknown'}
                              </span>
                              <span className="text-xs text-gray-400 truncate block">
                                {contact.email}
                              </span>
                            </div>
                          </div>
                          <DillButton
                            label="Email"
                            onClick={() => handleSelectEmailAddress(contact.email!)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {buyerEmailAddresses.length > 0 && (
                    <div
                      className={
                        contactsWithEmail.length > 0 ? 'border-t border-gray-100 py-1.5' : 'py-1.5'
                      }>
                      <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Saved Emails
                      </p>
                      {buyerEmailAddresses.map((addr) => (
                        <div
                          key={addr}
                          className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 group/row transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <EnvelopeSimple size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{addr}</span>
                          </div>
                          <DillButton
                            label="Email"
                            onClick={() => handleSelectEmailAddress(addr)}
                            variant="primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    className={
                      buyerEmailAddresses.length > 0 || contactsWithEmail.length > 0
                        ? 'border-t border-gray-100'
                        : ''
                    }>
                    <div className="p-3">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Enter Email
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={manualEmailInput}
                          onChange={(e) => setManualEmailInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualEmailInput.trim()) {
                              handleSelectEmailAddress(manualEmailInput.trim());
                            }
                          }}
                          placeholder="name@company.com"
                          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all"
                          autoFocus
                        />
                        <DillButton
                          label="Email"
                          onClick={() => {
                            if (manualEmailInput.trim()) {
                              handleSelectEmailAddress(manualEmailInput.trim());
                            }
                          }}
                          isDisabled={!manualEmailInput.trim()}
                          variant="primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DillButton
              label="Note"
              icon={<Plus size={12} weight="bold" />}
              onClick={() => setIsAddNoteModalOpen(true)}
              variant="secondary"
            />
            <DillButton
              label="Call log"
              icon={<Plus size={12} weight="bold" />}
              onClick={() => setIsAddCallModalOpen(true)}
              variant="secondary"
            />
            <DillButton
              label="Text log"
              icon={<Plus size={12} weight="bold" />}
              onClick={() => setIsAddTextMessageModalOpen(true)}
              variant="secondary"
            />
            <DillButton
              label="Task"
              icon={<Plus size={12} weight="bold" />}
              onClick={() => setIsAddTaskModalOpen(true)}
              variant="secondary"
            />
            <DillButton
              label="Promise"
              icon={<Plus size={12} weight="bold" />}
              onClick={() => setIsAddPromiseModalOpen(true)}
              variant="secondary"
            />
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="mb-5">
        <div className="flex gap-1 bg-gray-100/80 p-1 rounded-lg" role="tablist">
          {(
            [
              { key: 'ALL' as const, label: 'All', icon: null },
              { key: 'NOTE' as const, label: 'Notes', icon: <ChatCircleText size={14} /> },
              { key: 'PHONE_CALL' as const, label: 'Calls', icon: <Phone size={14} /> },
              { key: 'TEXT_MESSAGE' as const, label: 'Texts', icon: <ChatCircleDots size={14} /> },
              { key: 'TASK' as const, label: 'Tasks', icon: <CheckCircle size={14} /> },
              { key: 'EMAILS' as const, label: 'Emails', icon: <Envelope size={14} /> },
            ] as const
          ).map((tab) => {
            const isActive = filterType === tab.key;
            const count = filterCounts[tab.key];
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setFilterType(tab.key)}
                className={twMerge(
                  'px-3.5 py-1.5 text-sm font-medium rounded-md transition-all duration-150 flex items-center gap-1.5',
                  isActive
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}>
                {tab.icon}
                {tab.label}
                {count > 0 && (
                  <span
                    className={twMerge(
                      'ml-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-xs tabular-nums leading-none px-1',
                      isActive ? 'bg-gray-100 text-gray-600' : 'bg-gray-200/60 text-gray-400'
                    )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="relative">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-gray-200 via-gray-200 to-transparent"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="relative flex gap-3.5 pl-10 animate-pulse">
                  <div className="absolute left-0 top-4 w-[30px] h-[30px] rounded-full bg-gray-200 ring-2 ring-white" />
                  <div className="flex-1 bg-white border border-gray-100/80 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="h-4 w-24 bg-gray-100 rounded" />
                      <div className="h-3 w-32 bg-gray-100 rounded" />
                    </div>
                    <div className="h-3.5 w-3/4 bg-gray-100 rounded" />
                    <div className="flex gap-2 pt-1">
                      <div className="h-5 w-16 bg-gray-100 rounded-full" />
                      <div className="h-5 w-20 bg-gray-100 rounded-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : sortedActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              {filterType === 'NOTE' ? (
                <ChatCircleText size={24} />
              ) : filterType === 'PHONE_CALL' ? (
                <Phone size={24} />
              ) : filterType === 'TEXT_MESSAGE' ? (
                <ChatCircleDots size={24} />
              ) : filterType === 'TASK' ? (
                <CheckCircle size={24} />
              ) : filterType === 'EMAILS' ? (
                <Envelope size={24} />
              ) : (
                <DotsThree size={24} />
              )}
            </div>
            <p className="text-sm font-medium text-gray-500">
              {filterType === 'NOTE'
                ? 'No notes yet'
                : filterType === 'PHONE_CALL'
                ? 'No calls logged'
                : filterType === 'TEXT_MESSAGE'
                ? 'No texts logged'
                : filterType === 'TASK'
                ? 'No tasks created'
                : filterType === 'EMAILS'
                ? 'No emails sent'
                : 'No activities yet'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {filterType === 'NOTE'
                ? 'Add a note to track important details'
                : filterType === 'PHONE_CALL'
                ? 'Log a call to keep a record of conversations'
                : filterType === 'TEXT_MESSAGE'
                ? 'Log a text message to keep a record of communications'
                : filterType === 'TASK'
                ? 'Create a task to track follow-ups'
                : filterType === 'EMAILS'
                ? 'Payment requests and reminders will appear here'
                : 'Activities will appear here as they occur'}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-gray-200 via-gray-200 to-transparent"></div>

            {/* Activity items */}
            <div className="space-y-3">
              {sortedActivities.map((activity) => {
                const config = activityTypeConfig[activity.activityType] || {
                  icon: <DotsThree size={16} weight="fill" />,
                  color: 'bg-slate-500 text-white',
                  ringColor: 'ring-slate-200',
                  label: activity.activityType,
                };
                const isManual =
                  activity.activityType === 'NOTE' ||
                  activity.activityType === 'PHONE_CALL' ||
                  activity.activityType === 'TEXT_MESSAGE' ||
                  activity.activityType === 'TASK';

                return (
                  <div key={activity.id} className="relative flex gap-3.5 pl-10 group">
                    {/* Icon */}
                    <div
                      className={twMerge(
                        'absolute left-0 top-4 w-[30px] h-[30px] rounded-full flex items-center justify-center ring-2 ring-white shadow-sm',
                        config.color,
                        config.ringColor
                      )}>
                      {config.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-white border border-gray-200 rounded-xl transition-colors hover:border-blue-200 hover:bg-blue-50/30">
                      {/* Header row */}
                      <div className="flex items-center justify-between px-3 pt-2.5 pb-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-gray-900">{config.label}</span>
                          {activity.source === 'AUTOMATIC' && (
                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-medium tracking-wide uppercase">
                              Auto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          {activity.activityType === 'TASK' &&
                            activity.taskDetails &&
                            (activity.taskDetails.status === 'PENDING' ||
                              activity.taskDetails.status === 'IN_PROGRESS') && (
                              <div
                                className="flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}>
                                {activity.taskDetails.isDillRecommended ? (
                                  <>
                                    <div data-tooltip-id={`accept-task-${activity.id}`}>
                                      <DillButton
                                        variant="icon-blue"
                                        icon={<Check size={14} />}
                                        onClick={() => handleAcceptTask(activity)}
                                        isLoading={
                                          updatingTaskId === activity.id &&
                                          updatingAction === 'accept'
                                        }
                                        isDisabled={!!updatingTaskId}
                                      />
                                      <ReactTooltip
                                        id={`accept-task-${activity.id}`}
                                        place="top"
                                        variant="dark"
                                        content="Accept recommendation"
                                      />
                                    </div>
                                    <div data-tooltip-id={`decline-task-${activity.id}`}>
                                      <DillButton
                                        variant="icon-default"
                                        icon={<X size={14} />}
                                        onClick={() => handleDeclineTask(activity)}
                                        isLoading={
                                          updatingTaskId === activity.id &&
                                          updatingAction === 'decline'
                                        }
                                        isDisabled={!!updatingTaskId}
                                      />
                                      <ReactTooltip
                                        id={`decline-task-${activity.id}`}
                                        place="top"
                                        variant="dark"
                                        content="Decline recommendation"
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {activity.taskDetails.status === 'PENDING' && (
                                      <div data-tooltip-id="start-task-tooltip">
                                        <DillButton
                                          variant="icon-default"
                                          icon={<Play size={14} />}
                                          onClick={() => handleStartTask(activity)}
                                          isLoading={
                                            updatingTaskId === activity.id &&
                                            updatingAction === 'start'
                                          }
                                          isDisabled={!!updatingTaskId}
                                        />
                                        <ReactTooltip
                                          id="start-task-tooltip"
                                          place="top"
                                          variant="dark"
                                          content="Start task"
                                        />
                                      </div>
                                    )}
                                    <div data-tooltip-id="complete-task-tooltip">
                                      <DillButton
                                        variant="icon-blue"
                                        icon={<CheckCircle size={14} />}
                                        onClick={() => handleCompleteTask(activity)}
                                        isLoading={
                                          updatingTaskId === activity.id &&
                                          updatingAction === 'complete'
                                        }
                                        isDisabled={!!updatingTaskId}
                                      />
                                      <ReactTooltip
                                        id="complete-task-tooltip"
                                        place="top"
                                        variant="dark"
                                        content="Complete task"
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Description */}
                      {getActivityDescription(activity) && (
                        <div className="px-3 pt-0.5">
                          <p className="text-sm text-gray-500">
                            {getActivityDescription(activity)}
                          </p>
                        </div>
                      )}

                      {/* Email preview for PAYMENT_REMINDER / STATEMENT */}
                      {(activity.activityType === 'PAYMENT_REMINDER' ||
                        activity.activityType === 'STATEMENT') &&
                        activity.reminderDetails?.emailBody && (
                          <div className="px-3 pt-1.5">
                            <button
                              onClick={() => toggleEmailPreview(activity.id)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium bg-transparent border-none cursor-pointer p-0">
                              <Envelope size={13} />
                              {expandedEmailIds.has(activity.id) ? 'Hide email' : 'View email'}
                            </button>
                            {expandedEmailIds.has(activity.id) && (
                              <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden">
                                {activity.reminderDetails.emailSubject && (
                                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 font-medium truncate">
                                    Subject: {activity.reminderDetails.emailSubject}
                                  </div>
                                )}
                                <iframe
                                  srcDoc={activity.reminderDetails.emailBody}
                                  title="Email preview"
                                  sandbox="allow-same-origin"
                                  className="w-full border-0"
                                  style={{ height: '480px' }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                      {/* Rich text content for notes */}
                      {activity.activityType === 'NOTE' && activity.noteDetails?.content && (
                        <div className="px-3 pt-2 pb-0.5">
                          <LexicalViewer
                            content={getContentString(activity.noteDetails.content) || ''}
                            className="text-sm text-gray-500"
                          />
                        </div>
                      )}

                      {/* Rich text content for call notes */}
                      {activity.activityType === 'PHONE_CALL' && activity.callDetails?.notes && (
                        <div className="px-3 pt-2 pb-0.5">
                          <LexicalViewer
                            content={getContentString(activity.callDetails.notes) || ''}
                            className="text-sm text-gray-500"
                          />
                        </div>
                      )}

                      {/* Rich text content for text message notes */}
                      {activity.activityType === 'TEXT_MESSAGE' &&
                        activity.textMessageDetails?.notes && (
                          <div className="px-3 pt-2 pb-0.5">
                            <LexicalViewer
                              content={getContentString(activity.textMessageDetails.notes) || ''}
                              className="text-sm text-gray-500"
                            />
                          </div>
                        )}

                      {/* Rich text content for task description */}
                      {activity.activityType === 'TASK' && activity.taskDetails?.description && (
                        <div className="px-3 pt-2 pb-0.5">
                          <LexicalViewer
                            content={getContentString(activity.taskDetails.description) || ''}
                            className="text-sm text-gray-500"
                          />
                        </div>
                      )}

                      {/* Footer: task details, links, references, user info */}
                      {((activity.activityType === 'TASK' && activity.taskDetails) ||
                        activity.activityType !== 'TASK' ||
                        activity.references?.length ||
                        activity.performedByUser) && (
                        <div className="mx-3 mt-2 pt-2 border-t border-gray-100 pb-2.5 space-y-2">
                          {/* Task details: due date, status, priority + date/edit */}
                          {activity.activityType === 'TASK' && activity.taskDetails && (
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                {activity.taskDetails.dueDate && (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg">
                                    <Calendar size={12} className="text-gray-400" />
                                    <span className="text-xs font-medium text-gray-500 tabular-nums">
                                      {moment(new Date(activity.taskDetails.dueDate)).format(
                                        'MMM D, YYYY'
                                      )}
                                    </span>
                                  </div>
                                )}
                                {activity.taskDetails.status && (
                                  <span
                                    className={twMerge(
                                      'text-xs font-medium px-2 py-0.5 rounded-full',
                                      activity.taskDetails.status === 'COMPLETED' &&
                                        'bg-emerald-50 text-emerald-700',
                                      activity.taskDetails.status === 'IN_PROGRESS' &&
                                        'bg-blue-50 text-blue-700',
                                      activity.taskDetails.status === 'PENDING' &&
                                        'bg-gray-50 text-gray-600',
                                      activity.taskDetails.status === 'CANCELLED' &&
                                        'bg-red-50 text-red-700'
                                    )}>
                                    {activity.taskDetails.status === 'IN_PROGRESS'
                                      ? 'In Progress'
                                      : activity.taskDetails.status.charAt(0) +
                                        activity.taskDetails.status.slice(1).toLowerCase()}
                                  </span>
                                )}
                                {activity.taskDetails.priority && (
                                  <span
                                    className={twMerge(
                                      'inline-flex items-center gap-1.5 text-xs font-medium',
                                      activity.taskDetails.priority === 'HIGH' && 'text-red-600',
                                      activity.taskDetails.priority === 'MEDIUM' &&
                                        'text-amber-600',
                                      activity.taskDetails.priority === 'LOW' && 'text-emerald-600'
                                    )}>
                                    <span
                                      className={twMerge(
                                        'w-1.5 h-1.5 rounded-full flex-shrink-0',
                                        activity.taskDetails.priority === 'HIGH' && 'bg-red-500',
                                        activity.taskDetails.priority === 'MEDIUM' &&
                                          'bg-amber-500',
                                        activity.taskDetails.priority === 'LOW' && 'bg-emerald-500'
                                      )}
                                    />
                                    {activity.taskDetails.priority.charAt(0) +
                                      activity.taskDetails.priority.slice(1).toLowerCase()}
                                  </span>
                                )}
                                {activity.taskDetails.isDillRecommended && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                                    <Star size={11} weight="fill" />
                                    Dill Recommended
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span
                                  className="text-xs text-gray-400 tabular-nums"
                                  title={
                                    activity.date
                                      ? moment(new Date(activity.date)).format(
                                          'MMM D, YYYY [at] h:mm A'
                                        )
                                      : ''
                                  }>
                                  {formatActivityDate(activity.date)}
                                </span>
                                {isManual && (
                                  <button
                                    onClick={() => {
                                      if (activity.activityType === 'TASK')
                                        setEditingTask(activity);
                                    }}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 opacity-1 group-hover:opacity-100 transition-all cursor-pointer bg-transparent border-none"
                                    title={`Edit ${config.label.toLowerCase()}`}
                                    aria-label={`Edit ${config.label.toLowerCase()}`}>
                                    <PencilSimple size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* View links */}
                          {activity.activityType === 'PAYMENT' &&
                            activity.paymentDetails?.relatedPaymentId && (
                              <button
                                onClick={() => {
                                  const transactionId = activity.paymentDetails?.relatedPaymentId;
                                  if (onViewPaymentDetails && transactionId) {
                                    onViewPaymentDetails(transactionId);
                                  } else {
                                    window.open(
                                      `/payments?transactionId=${transactionId}`,
                                      '_blank'
                                    );
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View payment details
                              </button>
                            )}

                          {activity.activityType === 'PAYMENT' &&
                            activity.paymentDetails?.paymentRequestId && (
                              <button
                                onClick={() => {
                                  const paymentRequestId =
                                    activity.paymentDetails?.paymentRequestId;
                                  if (onViewPaymentRequest && paymentRequestId) {
                                    onViewPaymentRequest(paymentRequestId);
                                  } else {
                                    window.open(
                                      `/payment-requests?paymentRequestId=${paymentRequestId}`,
                                      '_blank'
                                    );
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View payment request
                              </button>
                            )}

                          {activity.activityType === 'REFUND' &&
                            activity.refundDetails?.relatedTransactionId && (
                              <button
                                onClick={() => {
                                  const transactionId =
                                    activity.refundDetails?.relatedTransactionId;
                                  if (onViewPaymentDetails && transactionId) {
                                    onViewPaymentDetails(transactionId);
                                  } else {
                                    window.open(
                                      `/payments?transactionId=${transactionId}`,
                                      '_blank'
                                    );
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View transaction details
                              </button>
                            )}

                          {activity.activityType === 'PAYMENT_REQUEST' &&
                            activity.reminderDetails?.paymentRequestId &&
                            activity.reminderDetails?.action !== 'DELETED' && (
                              <button
                                onClick={() => {
                                  const paymentRequestId =
                                    activity.reminderDetails?.paymentRequestId;
                                  if (onViewPaymentRequest && paymentRequestId) {
                                    onViewPaymentRequest(paymentRequestId);
                                  } else {
                                    window.open(
                                      `/payment-requests?paymentRequestId=${paymentRequestId}`,
                                      '_blank'
                                    );
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View payment request
                              </button>
                            )}

                          {activity.activityType === 'DISPUTE' &&
                            activity.disputeDetails?.disputeId && (
                              <button
                                onClick={() => {
                                  const disputeId = activity.disputeDetails?.disputeId;
                                  if (onViewDisputeDetails && disputeId) {
                                    onViewDisputeDetails(disputeId);
                                  } else {
                                    window.open(`/disputes?disputeId=${disputeId}`, '_blank');
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View dispute details
                              </button>
                            )}

                          {activity.activityType === 'PROMISE_TO_PAY' &&
                            activity.promiseToPayDetails?.promiseToPayId && (
                              <button
                                onClick={() => {
                                  const promiseToPayId =
                                    activity.promiseToPayDetails?.promiseToPayId;
                                  if (onViewPromiseToPay && promiseToPayId) {
                                    onViewPromiseToPay(promiseToPayId);
                                  } else {
                                    window.open(
                                      `/promiseToPays?promiseToPayId=${promiseToPayId}`,
                                      '_blank'
                                    );
                                  }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <ArrowSquareOut size={14} />
                                View promise
                              </button>
                            )}

                          {/* Reference chips */}
                          {activity.references && activity.references.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {activity.references.map((ref, idx) => (
                                <span
                                  key={`${ref.type}-${ref.id}-${idx}`}
                                  className={twMerge(
                                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                                    ref.type === 'INVOICE'
                                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                                      : ref.type === 'CONTACT'
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-blue-50 text-blue-500 border-blue-200'
                                  )}>
                                  {ref.type === 'INVOICE' ? (
                                    <FileText size={11} weight="fill" />
                                  ) : ref.type === 'CONTACT' ? (
                                    <AddressBook size={11} weight="fill" />
                                  ) : (
                                    <Buildings size={11} weight="fill" />
                                  )}
                                  {ref.description || ref.id}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* User info + Date for non-task activities */}
                          {!(activity.activityType === 'TASK' && activity.taskDetails) && (
                            <div className="flex items-center justify-between gap-2">
                              {activity.performedByUser ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-2xs font-semibold text-gray-500 uppercase">
                                    {activity.performedByUser.firstName?.[0]}
                                    {activity.performedByUser.lastName?.[0]}
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {activity.performedByUser.firstName}{' '}
                                    {activity.performedByUser.lastName}
                                  </span>
                                </div>
                              ) : (
                                <div />
                              )}
                              <div className="flex items-center gap-1 shrink-0">
                                <span
                                  className="text-xs text-gray-400 tabular-nums"
                                  title={
                                    activity.date
                                      ? moment(new Date(activity.date)).format(
                                          'MMM D, YYYY [at] h:mm A'
                                        )
                                      : ''
                                  }>
                                  {formatActivityDate(activity.date)}
                                </span>
                                {isManual && (
                                  <button
                                    onClick={() => {
                                      if (activity.activityType === 'NOTE')
                                        setEditingNote(activity);
                                      if (activity.activityType === 'PHONE_CALL')
                                        setEditingCall(activity);
                                      if (activity.activityType === 'TEXT_MESSAGE')
                                        setEditingTextMessage(activity);
                                    }}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 opacity-1 group-hover:opacity-100 transition-all cursor-pointer bg-transparent border-none"
                                    title={`Edit ${config.label.toLowerCase()}`}
                                    aria-label={`Edit ${config.label.toLowerCase()}`}>
                                    <PencilSimple size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {/* User info for task activities */}
                          {activity.activityType === 'TASK' &&
                            activity.taskDetails &&
                            activity.performedByUser && (
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-2xs font-semibold text-gray-500 uppercase">
                                  {activity.performedByUser.firstName?.[0]}
                                  {activity.performedByUser.lastName?.[0]}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {activity.performedByUser.firstName}{' '}
                                  {activity.performedByUser.lastName}
                                </span>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddNoteModal
        open={isAddNoteModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddNoteModalOpen(false)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AddCallModal
        open={isAddCallModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddCallModalOpen(false)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AddCallModal
        open={makeCallPhoneNumber !== null}
        buyerId={buyerId}
        phoneNumber={makeCallPhoneNumber ?? ''}
        callingMode
        onClose={() => setMakeCallPhoneNumber(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AddTaskModal
        open={isAddTaskModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddTaskModalOpen(false)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <PromiseToPayFormModal
        open={isAddPromiseModalOpen}
        onClose={() => setIsAddPromiseModalOpen(false)}
        onSubmit={handleCreatePromise}
        isLoading={promiseToPayLoadingList.includes('createPromiseToPay')}
        supplierId={currentSupplier?.id || ''}
        buyerId={buyerId}
        contacts={buyer?.buyerContacts ?? []}
      />
      <EditNoteModal
        open={editingNote !== null}
        activity={editingNote}
        onClose={() => setEditingNote(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <EditCallModal
        open={editingCall !== null}
        activity={editingCall}
        onClose={() => setEditingCall(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <EditTaskModal
        open={editingTask !== null}
        activity={editingTask}
        onClose={() => setEditingTask(null)}
      />
      <AddTextMessageModal
        open={isAddTextMessageModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddTextMessageModalOpen(false)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AddTextMessageModal
        open={sendTextPhoneNumber !== null}
        buyerId={buyerId}
        phoneNumber={sendTextPhoneNumber ?? ''}
        sendingMode
        onClose={() => setSendTextPhoneNumber(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <EditTextMessageModal
        open={editingTextMessage !== null}
        activity={editingTextMessage}
        onClose={() => setEditingTextMessage(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AddEmailModal
        open={sendEmailAddress !== null}
        buyerId={buyerId}
        emailAddress={sendEmailAddress ?? ''}
        sendingMode
        onClose={() => setSendEmailAddress(null)}
        contacts={buyer?.buyerContacts ?? []}
      />
      <AppDateRangePickerModal
        open={isDateRangeModalOpen}
        handleClose={() => setIsDateRangeModalOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange(range);
          setIsDateRangeModalOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
}

export default CollectionActivityTimeline;
